import { FC, useState } from 'react'
import { PersonalProfileFragment } from '../../../types'
import { Divider, Card, Switch, Tooltip } from 'antd'
import { EmployeeInformationComponent } from '../personal/EmployeeInformationComponent'
import { EducationTable } from '../personal/EducationTable'
import { AdressesTable } from '../personal/AdressesTable'
import { EmergencyContactsTable } from '../personal/EmergencyContactsTable'
import PerText from '../../../components/custom_components/Text'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { useTranslation, Trans } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { refreshMySkillStatus } from '../../../graphql/skill-status/useSkillStatusMutations'
import { SkillStatusLabelComponent, SkillStatusDescription } from '../../skillStatus/SkillStatusLabelComponent'
import { KudosCarousel } from '../../../components/custom_components/Carousel/KudosCarousel'
import { useMyKudos } from '../../../graphql/kudos/useKudosForEmployeeWithCache'
import { useHistory } from 'react-router'
import { hasPermission } from '../../../shared/permissions/permission-helper'
import { SidebarPermissions } from '../../../routes/permissions'
import useIsFeatureEnabled from '../../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../../graphql/featureToggle/FeatureNameEnum'
import MyKudosOptOutModal from '../../../components/custom_components/KudosOptOutModal/MyKudosOptOutModal'
import { EmployeeActivityListComponent } from '../../badge/EmployeeActivityListComponent'

interface PrivatePersonalDataTabProps {
  profile: PersonalProfileFragment
}

export const PrivatePersonalDataTab: FC<PrivatePersonalDataTabProps> = ({ profile }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const history = useHistory()
  const { isFeatureEnabled: isKudosEnvVarEnabled } = useIsFeatureEnabled(FeatureNameEnum.KUDOS)
  const timezone = {
    locale: user.locale,
    timezone: user.timezone,
  }
  const userRoles = user.roles
  const [isRefreshMySkillStatusProcessing, toggleRefreshMySkillStatusProcessing] = useState<boolean>(false)
  const [isUpdateOptOutModalVisible, setIsUpdateOptOutModalVisible] = useState<boolean>(false)
  const [newOptOutValue, setNewOptOutValue] = useState<boolean | undefined>(undefined)
  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)

  const {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: kudosLoading,
    error: kudosError,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    onKudosOptOutValueUpdated,
  } = useMyKudos()

  return (
    <>
      <EmployeeInformationComponent profile={profile} publicProfile={false} timezone={timezone} />
      <Divider />

      <PerText appearance="tile-heading" mb="s">
        {t('profile.skillCenter.private.header')}
      </PerText>

      <Card bordered={false}>
        <PerFlexRow align="middle">
          <PerText mb="xxs">
            <Trans
              i18nKey="profile.skillCenter.private.desc"
              components={[
                <a
                  style={{ textDecoration: 'underline', textTransform: 'uppercase', fontWeight: 'bold' }}
                  href={`${profile.skillCenterUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {''}
                </a>,
              ]}
            />
          </PerText>
        </PerFlexRow>
        <SkillStatusLabelComponent
          skillStatus={profile.skillStatus}
          isProcessing={isRefreshMySkillStatusProcessing}
          skillStatusErrorVisible={skillStatusError}
        />
      </Card>

      <SkillStatusDescription
        skillStatus={profile.skillStatus}
        skillStatusErrorVisible={skillStatusError}
        onClick={async () => {
          toggleRefreshMySkillStatusProcessing(true)
          const { data: skillStatusData, errors: skillStatusErrors } = await refreshMySkillStatus(profile.employeeId)
          if (!skillStatusData && skillStatusErrors) {
            setSkillStatusError(true)
          }
          toggleRefreshMySkillStatusProcessing(false)
        }}
        isProcessing={isRefreshMySkillStatusProcessing}
      />
      {profile.activityListEnabled && (
        <>
          <Divider />

          <EmployeeActivityListComponent employeeId={profile.employeeId} title={t('profile.activityList.privateHeader')} />
        </>
      )}

      {isKudosEnvVarEnabled && isKudosEnabled && (
        <>
          <MyKudosOptOutModal
            visible={isUpdateOptOutModalVisible && newOptOutValue !== undefined}
            closeModal={() => {
              setIsUpdateOptOutModalVisible(false)
              setNewOptOutValue(undefined)
            }}
            newOptOutValue={newOptOutValue!}
            onKudosOptOutValueUpdated={onKudosOptOutValueUpdated}
          />
          <Divider />

          <PerFlexRow justify="space-between" align="middle" mb="xs">
            <PerText appearance="tile-heading">{t('kudos.header')}</PerText>

            <PerFlexRow align="middle" direction="column" justify="center">
              <Tooltip
                title={
                  employeeOptedOut
                    ? t('kudos.profileSection.optOut.tooltipButton.enable')
                    : t('kudos.profileSection.optOut.tooltipButton.disable')
                }
              >
                <Switch
                  checked={!employeeOptedOut}
                  onChange={() => {
                    setNewOptOutValue(!employeeOptedOut)
                    setIsUpdateOptOutModalVisible(true)
                  }}
                />
              </Tooltip>
            </PerFlexRow>
          </PerFlexRow>

          {!employeeOptedOut && (
            <>
              <PerText color="secondary">
                <Trans
                  i18nKey="kudos.profileSection.description"
                  components={[
                    <a
                      key="kudos-link"
                      onClick={(e) => {
                        e.preventDefault()
                        history.push(
                          hasPermission(SidebarPermissions.sidemenuKudos, userRoles ? userRoles : []) ? '/kudos' : '/kudos-replyer',
                        )
                      }}
                      style={{ color: '#05cbce', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Kudos page
                    </a>,
                  ]}
                />
              </PerText>
              <PerText color="secondary" mb="s">
                {t('kudos.profileSection.optOut.privateOptedInDescription')}
              </PerText>
            </>
          )}

          <KudosCarousel
            privateProfileView
            employeeOptedOut={employeeOptedOut}
            kudos={currentKudos}
            nameDisplayPreference={user.nameDisplayPreference}
            loading={kudosLoading}
            error={kudosError || null}
            currentPage={currentPage}
            totalPages={totalPages}
            onNextPage={goToNextPage}
            onPreviousPage={goToPreviousPage}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
          />
        </>
      )}

      {profile.address && profile.address.length > 0 && (
        <>
          <Divider />
          <AdressesTable addresses={profile.address} />
        </>
      )}
      {profile.emergencyContacts && profile.emergencyContacts.length > 0 && (
        <>
          <Divider />
          <EmergencyContactsTable emergencyContacts={profile.emergencyContacts} />
        </>
      )}

      {profile.education && profile.education.length > 0 && (
        <>
          <Divider />
          <EducationTable education={profile.education} timezone={timezone} />
        </>
      )}
    </>
  )
}
